import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, TextField, Button, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel,
  FormControl, InputLabel, Select, MenuItem, Checkbox, Snackbar, Alert,
} from '@mui/material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const today = new Date().toISOString().split('T')[0];

const initialNovoAdiantamento = {
  funcionario_id: '',
  tipo_id: '',
  valor_total: '',
  quantidade_parcelas: 1,
  data_adiantamento: today,
  data_primeira_parcela: today,
};


// ─── Componente interno: gerenciar tipos ─────────────────────────────────────
function TiposPanel({ tipos, novoTipoDesc, setNovoTipoDesc, selectedTipoId, editandoTipo,
                      onSelectTipo, onEditChange, onSalvarNovo, onSalvarEdit }) {
  const [subTab, setSubTab] = useState(0);
  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Adicionar Tipo" />
        <Tab label="Editar Tipo" />
      </Tabs>

      {/* Adicionar */}
      {subTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
          <Typography variant="h6">Adicionar novo Tipo de Lançamento</Typography>
          <TextField
            label="Descrição"
            value={novoTipoDesc}
            onChange={(e) => setNovoTipoDesc(e.target.value)}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={onSalvarNovo}>Salvar</Button>
            <Button variant="outlined" onClick={() => setNovoTipoDesc('')}>Cancelar</Button>
          </Box>
        </Box>
      )}

      {/* Editar */}
      {subTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
          <Typography variant="h6">Editar Tipo de Lançamento</Typography>
          <FormControl fullWidth>
            <InputLabel>Selecione um Tipo</InputLabel>
            <Select
              value={selectedTipoId}
              label="Selecione um Tipo"
              onChange={(e) => onSelectTipo(e.target.value)}
            >
              <MenuItem value=""><em>Nenhum</em></MenuItem>
              {tipos.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.descricao}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {editandoTipo ? (
            <>
              <TextField label="ID" value={editandoTipo.id} disabled fullWidth />
              <TextField
                label="Descrição"
                value={editandoTipo.descricao}
                onChange={(e) => onEditChange(e.target.value)}
                fullWidth
              />
              <Button variant="contained" onClick={onSalvarEdit}>Salvar Alterações</Button>
            </>
          ) : (
            <Alert severity="info">Selecione um tipo para editar.</Alert>
          )}
        </Box>
      )}
    </Box>
  );
}

function PontoAdiantamento() {
  const [tabIndex, setTabIndex] = useState(0);
  const { authenticatedFetch } = useAuth();

  // Opções
  const [funcionarios, setFuncionarios]   = useState([]); // filtro: quem tem adiantamento
  const [todosFuncionarios, setTodosFuncionarios] = useState([]); // formulário: todos ativos
  const [tipos, setTipos]                 = useState([]);
  const [opcoesAnos, setOpcoesAnos]       = useState([]);
  const [opcoesMeses, setOpcoesMeses]     = useState([]);

  // Filtros
  const [filtroFunc, setFiltroFunc] = useState('');
  const [filtroAno, setFiltroAno]   = useState('');
  const [filtroMes, setFiltroMes]   = useState('');

  // Tabela
  const [adiantamentos, setAdiantamentos] = useState([]);
  const [sortField, setSortField] = useState('data_parcela');
  const [sortDir, setSortDir] = useState('asc');

  // Formulário
  const [novo, setNovo] = useState(initialNovoAdiantamento);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Gestão de tipos
  const [novoTipoDesc, setNovoTipoDesc] = useState('');
  const [editandoTipo, setEditandoTipo] = useState(null);
  const [selectedTipoId, setSelectedTipoId] = useState('');

  // ─── Valor parcela calculado ─────────────────────────────────────────────────
  const valorParcela =
    novo.valor_total && novo.quantidade_parcelas > 0
      ? (parseFloat(novo.valor_total) / parseInt(novo.quantidade_parcelas)).toFixed(2)
      : '';

  // ─── Fetch helpers ───────────────────────────────────────────────────────────
  const fetchFuncionarios = useCallback(async (ano = '') => {
    try {
      // Filtro: só funcionários que possuem adiantamento
      const res = await authenticatedFetch(
        `${API_BASE_URL}/api/ponto-adiantamento-buscar-funcionarios-com-adiantamento/`
      );
      if (res.ok) setFuncionarios(await res.json());
    } catch (err) { console.error(err); }
  }, [authenticatedFetch]);

  const fetchAnos = useCallback(async (funcionarioId = '') => {
    try {
      const url = funcionarioId
        ? `${API_BASE_URL}/api/ponto-adiantamento-buscar-ano/?funcionario_id=${funcionarioId}`
        : `${API_BASE_URL}/api/ponto-adiantamento-buscar-ano/`;
      const res = await authenticatedFetch(url);
      if (res.ok) setOpcoesAnos(await res.json());
    } catch (err) { console.error(err); }
  }, [authenticatedFetch]);

  const fetchMeses = useCallback(async (ano, funcionarioId = '') => {
    if (!ano) { setOpcoesMeses([]); return; }
    try {
      const params = new URLSearchParams({ ano });
      if (funcionarioId) params.append('funcionario_id', funcionarioId);
      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-adiantamento-buscar-mes/?${params}`);
      if (res.ok) setOpcoesMeses(await res.json());
    } catch (err) { console.error(err); }
  }, [authenticatedFetch]);

  const fetchTabela = useCallback(async (funcionarioId = '', ano = '', mes = '') => {
    try {
      const params = new URLSearchParams();
      if (funcionarioId) params.append('funcionario_id', funcionarioId);
      if (ano) params.append('ano', ano);
      if (mes) params.append('mes', mes);
      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-adiantamento-buscar/?${params}`);
      if (res.ok) setAdiantamentos(await res.json());
    } catch (err) { console.error(err); }
  }, [authenticatedFetch]);

  const fetchTipos = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-adiantamento-tipo-adiantamento-buscar/`);
      if (res.ok) setTipos(await res.json());
    } catch (err) { console.error(err); }
  }, [authenticatedFetch]);

  // ─── Carga inicial ───────────────────────────────────────────────────────────
  useEffect(() => {
    fetchFuncionarios();
    fetchAnos();
    fetchTabela();
    // Todos os funcionários ativos para o formulário de novo adiantamento
    const fetchTodos = async () => {
      try {
        const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-adiantamento-buscar-funcionarios/`);
        if (res.ok) setTodosFuncionarios(await res.json());
      } catch (err) { console.error(err); }
    };
    fetchTodos();
    fetchTipos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recarrega tabela ao voltar para aba 0
  useEffect(() => {
    if (tabIndex === 0) fetchTabela(filtroFunc, filtroAno, filtroMes);
  }, [tabIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers filtro ─────────────────────────────────────────────────────────
  const handleFiltroFuncChange = async (v) => {
    setFiltroFunc(v); setFiltroAno(''); setFiltroMes(''); setOpcoesMeses([]);
    await fetchAnos(v);
    fetchTabela(v, '', '');
  };

  const handleFiltroAnoChange = async (v) => {
    setFiltroAno(v); setFiltroMes('');
    if (v) { await fetchFuncionarios(v); await fetchMeses(v, filtroFunc); }
    else   { await fetchFuncionarios(''); setOpcoesMeses([]); }
    fetchTabela(filtroFunc, v, '');
  };

  const handleFiltroMesChange = (v) => {
    setFiltroMes(v);
    fetchTabela(filtroFunc, filtroAno, v);
  };

  // ─── Toggle pago ─────────────────────────────────────────────────────────────
  const handleTogglePago = async (id, statusAtual) => {
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/adiantamento-pagar/${id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parcela_paga: !statusAtual }),
      });
      if (res.ok) fetchTabela(filtroFunc, filtroAno, filtroMes);
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao atualizar pagamento', severity: 'error' });
    }
  };

  // ─── Handlers formulário ─────────────────────────────────────────────────────
  const handleNovoChange = (e) => {
    const { name, value } = e.target;
    setNovo(prev => {
      const updated = { ...prev, [name]: value };
      // Ao alterar a data do adiantamento, espelha na primeira parcela
      // (só se o usuário ainda não alterou a primeira parcela manualmente)
      if (name === 'data_adiantamento' && prev.data_primeira_parcela === prev.data_adiantamento) {
        updated.data_primeira_parcela = value;
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!novo.funcionario_id || !novo.tipo_id || !novo.valor_total) {
      setSnackbar({ open: true, message: 'Preencha funcionário, tipo e valor.', severity: 'warning' });
      return;
    }
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-adiantamento-novo-adiantamento-registrar/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...novo, valor_parcela: valorParcela }),
      });
      if (!res.ok) throw new Error();
      setSnackbar({ open: true, message: 'Adiantamento registrado com sucesso!', severity: 'success' });
      setNovo(initialNovoAdiantamento);
      setTabIndex(0);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao registrar adiantamento.', severity: 'error' });
    }
  };

  // ─── Ordenação ───────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    setSortDir(prev => (sortField === field && prev === 'asc') ? 'desc' : 'asc');
    setSortField(field);
  };

  const sortedAdiantamentos = [...adiantamentos].sort((a, b) => {
    let va = a[sortField] ?? '';
    let vb = b[sortField] ?? '';
    // Comparação de string (funciona para datas ISO e nomes)
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // ─── Handlers tipo ───────────────────────────────────────────────────────────
  const handleSalvarNovoTipo = async () => {
    if (!novoTipoDesc.trim()) {
      setSnackbar({ open: true, message: 'Informe a descrição do tipo.', severity: 'warning' });
      return;
    }
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/adiantamento-tipo-registrar/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ descricao: novoTipoDesc }),
      });
      if (!res.ok) throw new Error();
      setSnackbar({ open: true, message: 'Tipo registrado com sucesso!', severity: 'success' });
      setNovoTipoDesc('');
      fetchTipos();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao registrar tipo.', severity: 'error' });
    }
  };

  const handleSelectTipo = (id) => {
    setSelectedTipoId(id);
    const tipo = tipos.find(t => t.id === id || t.id === Number(id));
    setEditandoTipo(tipo ? { ...tipo } : null);
  };

  const handleSalvarEditTipo = async () => {
    if (!editandoTipo?.descricao?.trim()) {
      setSnackbar({ open: true, message: 'Informe a descrição.', severity: 'warning' });
      return;
    }
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/adiantamento-tipo-editar/${editandoTipo.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ descricao: editandoTipo.descricao }),
      });
      if (!res.ok) throw new Error();
      setSnackbar({ open: true, message: 'Tipo atualizado com sucesso!', severity: 'success' });
      fetchTipos();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao atualizar tipo.', severity: 'error' });
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Adiantamentos" />
        <Tab label="Novo Adiantamento" />
        <Tab label="Tipos de Lançamento" />
      </Tabs>

      {/* ── ABA 0: TABELA ────────────────────────────────────────────────────── */}
      <TabPanel value={tabIndex} index={0}>
        {/* Filtros */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Funcionário</InputLabel>
            <Select value={filtroFunc} label="Funcionário" onChange={(e) => handleFiltroFuncChange(e.target.value)}>
              <MenuItem value=""><em>Todos</em></MenuItem>
              {funcionarios.map(f => <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 110 }}>
            <InputLabel>Ano</InputLabel>
            <Select value={filtroAno} label="Ano" onChange={(e) => handleFiltroAnoChange(e.target.value)}>
              <MenuItem value=""><em>Todos</em></MenuItem>
              {opcoesAnos.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }} disabled={!filtroAno}>
            <InputLabel>Mês</InputLabel>
            <Select value={filtroMes} label="Mês" onChange={(e) => handleFiltroMesChange(e.target.value)}>
              <MenuItem value=""><em>Todos</em></MenuItem>
              {opcoesMeses.map(m => (
                <MenuItem key={m} value={m}>
                  {String(m).padStart(2, '0')} — {new Date(0, m - 1).toLocaleString('pt-BR', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Tabela */}
        <TableContainer component={Paper} sx={{ maxHeight: 500, width: 900 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortField === 'funcionario_nome' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'funcionario_nome'}
                    direction={sortField === 'funcionario_nome' ? sortDir : 'asc'}
                    onClick={() => handleSort('funcionario_nome')}
                  >Funcionário</TableSortLabel>
                </TableCell>
                <TableCell align="right">Valor Total</TableCell>
                <TableCell align="right">Valor Parcela</TableCell>
                <TableCell align="center">Qtd Parcelas</TableCell>
                <TableCell align="center">Nº Parcela</TableCell>
                <TableCell sortDirection={sortField === 'data' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'data'}
                    direction={sortField === 'data' ? sortDir : 'asc'}
                    onClick={() => handleSort('data')}
                  >Data Acordo</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'data_parcela' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'data_parcela'}
                    direction={sortField === 'data_parcela' ? sortDir : 'asc'}
                    onClick={() => handleSort('data_parcela')}
                  >Data Parcela</TableSortLabel>
                </TableCell>
                <TableCell align="center">Pago</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAdiantamentos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Nenhum adiantamento encontrado.
                  </TableCell>
                </TableRow>
              ) : sortedAdiantamentos.map((row) => (
                <TableRow key={row.adiantamento_id} hover>
                  <TableCell>{row.funcionario_nome}</TableCell>
                  <TableCell align="right">
                    {Number(row.valor_total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                  <TableCell align="right">
                    {Number(row.valor_parcela).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                  <TableCell align="center">{row.quantidade_parcelas}</TableCell>
                  <TableCell align="center">{row.numero_parcela}</TableCell>
                  <TableCell>{new Date(row.data + 'T12:00:00').toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell>{new Date(row.data_parcela + 'T12:00:00').toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={!!row.parcela_paga}
                      onChange={() => handleTogglePago(row.adiantamento_id, row.parcela_paga)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* ── ABA 1: NOVO ADIANTAMENTO ─────────────────────────────────────────── */}
      <TabPanel value={tabIndex} index={1}>
        <Typography variant="h6" sx={{ mb: 3 }}>Novo Adiantamento</Typography>

        {/* Layout fiel ao C#: coluna de campos à esquerda */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 420 }}>

          {/* Funcionário */}
          <FormControl fullWidth>
            <InputLabel>Funcionário</InputLabel>
            <Select
              name="funcionario_id"
              value={novo.funcionario_id}
              label="Funcionário"
              onChange={handleNovoChange}
            >
              <MenuItem value=""><em>Selecione</em></MenuItem>
              {todosFuncionarios.map(f => <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Tipo */}
          <FormControl fullWidth>
            <InputLabel>Tipo de Lançamento</InputLabel>
            <Select
              name="tipo_id"
              value={novo.tipo_id}
              label="Tipo de Lançamento"
              onChange={handleNovoChange}
            >
              <MenuItem value=""><em>Selecione</em></MenuItem>
              {tipos.map(t => <MenuItem key={t.id} value={t.id}>{t.descricao}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Data do Adiantamento */}
          <TextField
            fullWidth
            label="Data do Adiantamento"
            name="data_adiantamento"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={novo.data_adiantamento}
            onChange={handleNovoChange}
          />

          {/* Valor Total */}
          <TextField
            fullWidth
            label="Valor Total"
            name="valor_total"
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            value={novo.valor_total}
            onChange={handleNovoChange}
          />

          {/* Quantidade de Parcelas */}
          <TextField
            fullWidth
            label="Quantidade de Parcelas"
            name="quantidade_parcelas"
            type="number"
            inputProps={{ min: 1, max: 60 }}
            value={novo.quantidade_parcelas}
            onChange={handleNovoChange}
          />

          {/* Valor da Parcela — calculado, somente leitura */}
          <TextField
            fullWidth
            label="Valor da Parcela"
            value={
              valorParcela
                ? Number(valorParcela).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                : ''
            }
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
          />

          {/* Primeira Parcela Em — espelha a data do adiantamento por padrão */}
          <TextField
            fullWidth
            label="Primeira Parcela Em"
            name="data_primeira_parcela"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={novo.data_primeira_parcela}
            onChange={handleNovoChange}
            helperText="Por padrão igual à data do adiantamento"
          />

          {/* Botão */}
          <Button variant="contained" size="large" onClick={handleSubmit} sx={{ mt: 1 }}>
            Adicionar Adiantamento
          </Button>
        </Box>
      </TabPanel>

      {/* ── ABA 2: TIPOS ─────────────────────────────────────────────────────── */}
      <TabPanel value={tabIndex} index={2}>
        <TiposPanel
          tipos={tipos}
          novoTipoDesc={novoTipoDesc}
          setNovoTipoDesc={setNovoTipoDesc}
          selectedTipoId={selectedTipoId}
          editandoTipo={editandoTipo}
          onSelectTipo={handleSelectTipo}
          onEditChange={(v) => setEditandoTipo(prev => ({ ...prev, descricao: v }))}
          onSalvarNovo={handleSalvarNovoTipo}
          onSalvarEdit={handleSalvarEditTipo}
        />
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default PontoAdiantamento;